import { useCallback, useEffect, useState } from "react";
import { Alert, AlertTitle, Box, Button, Stack, Typography } from "@mui/material";

import { Loading, Permission, hostConfig } from "@lopie/kernel-ui";

import GuestView from "./GuestView.jsx";
import ManagerView from "./ManagerView.jsx";

/**
 * Hospitality: rentals and campsites, for a property manager and their guests.
 *
 * Everything here (units, rates, blocked dates, bookings, maintenance issues,
 * ratings) is this app's own data, held by its own server. Kernel has no
 * types for any of it, so this app reads no Kernel records at all. The only
 * Kernel calls it makes are the sends (email.send, message.send), which its
 * server makes with the signed-in person's own token for this app.
 *
 * Who sees what is decided by this app's server: a property manager sees
 * everything; a guest sees every unit's open dates and the published ratings,
 * and only their own bookings and issues.
 */
export default function App({ token, claims, onSignOut }) {
  const base = hostConfig()?.appHospitalityApi || "";
  const [state, setState] = useState(null);
  const [failure, setFailure] = useState(null);

  const call = useCallback(
    async (method, path, body) => {
      const r = await fetch(base + path, {
        method,
        headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      const text = await r.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
      if (!r.ok) {
        const e = new Error(data?.detail || data?.error || `The app's server answered ${r.status}`);
        e.status = r.status;
        e.body = data;
        throw e;
      }
      return data;
    },
    [base, token],
  );

  const load = useCallback(async () => {
    try {
      setState(await call("GET", "/state"));
      setFailure(null);
    } catch (e) {
      setFailure(e);
    }
  }, [call]);

  const act = useCallback(
    async (op, args = {}) => {
      const res = await call("POST", "/act", { op, ...args });
      await load();
      return res;
    },
    [call, load],
  );

  useEffect(() => {
    load();
  }, [load]);

  if (failure && !state) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          variant="outlined"
          action={<Button onClick={load}>Try again</Button>}
        >
          <AlertTitle>Hospitality&apos;s own server did not answer</AlertTitle>
          {failure.message}. This app keeps its bookings on its own server, not
          in Kernel, so this is that server failing to answer, not a decision
          about what you may see.
        </Alert>
      </Box>
    );
  }
  if (!state) return <Loading label="Loading your stays" minHeight="60vh" />;

  const manager = Boolean(state.me?.is_manager);
  const gate = manager
    ? "You see every unit, booking, maintenance issue and rating because you are "
      + "one of this app's property managers. That list is kept by this app; "
      + "Kernel's permission tags do not decide it."
    : "You see the open dates of every place, the ratings guests have published, "
      + "and only your own bookings and maintenance reports. This app decides "
      + "that from who you signed in as; Kernel's permission tags do not.";

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, pb: 8, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}
      >
        <Box>
          <Typography variant="h1">Hospitality</Typography>
          <Typography variant="body2" color="text.secondary">
            {manager ? "Property manager view" : "Guest view"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {state.me?.username || claims?.username || ""}
          </Typography>
          <Typography
            component="button"
            variant="body2"
            onClick={onSignOut}
            sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
          >
            Sign out
          </Typography>
        </Stack>
      </Stack>

      <Permission gate={gate} groups={claims?.["cognito:groups"] || []} />

      {failure ? (
        <Alert severity="warning" variant="outlined" action={<Button onClick={load}>Reload</Button>}>
          The last refresh failed ({failure.message}); what is shown may be out of date.
        </Alert>
      ) : null}

      {manager ? <ManagerView state={state} act={act} /> : <GuestView state={state} act={act} />}
    </Stack>
  );
}
